import { useMemo } from 'react';
import { useGetSummaryMessagesByMonth } from '@sendible/frontendv2/src/models/calendar';
import { getDaysInMonth, getFirstDayOfWeek } from '../../../../utils';
import { useCalendarContext } from '../../../../context';

type DayCell = { date: Date; isCurrentMonth: boolean };

// Stable fallback reference: `data || []` would create a new array on every render while
// the query is loading, which breaks memoization for anything keyed on `messageByDate`.
const EMPTY_MESSAGES: CalendarData[] = [];

export const useGetWeeks = () => {
  const { currentDate, postsPerDay, userWeekStartDay } = useCalendarContext();
  const { data } = useGetSummaryMessagesByMonth(currentDate, userWeekStartDay);
  const messages = data || EMPTY_MESSAGES;

  const [yearStr, monthStr] = currentDate.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;

  const computed = useMemo(() => {
    const messageByDate: Record<string, CalendarMessage[]> = {};
    const messageCountByDate: Record<string, number> = {};
    const failedMessageCountByDate: Record<string, number> = {};
    const visibleFailedCountByDate: Record<string, number> = {};

    messages.forEach(({ date, messageCount, failedMessageCount, messages: dayMessages }) => {
      messageCountByDate[date] = messageCount;
      failedMessageCountByDate[date] = failedMessageCount;

      // The summary is fetched at the density's maximum so changing postsPerDay doesn't need a refetch.
      const visibleMessages = dayMessages.slice(0, postsPerDay);

      messageByDate[date] = visibleMessages;
      // failedMessageCount counts failed posts, so count visible failed posts (not their failed destinations).
      visibleFailedCountByDate[date] = visibleMessages.filter((message) => (message.deliveryStatus?.failed ?? 0) > 0).length;
    });

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfWeek = getFirstDayOfWeek(year, month, userWeekStartDay);

    const leading: DayCell[] =
      firstDayOfWeek > 0
        ? Array.from({ length: firstDayOfWeek }, (_, i) => {
            const dayNum = new Date(year, month, 0).getDate() - firstDayOfWeek + i + 1;

            return { date: new Date(year, month - 1, dayNum), isCurrentMonth: false };
          })
        : [];

    const current: DayCell[] = Array.from({ length: daysInMonth }, (_, i) => ({
      date: new Date(year, month, i + 1),
      isCurrentMonth: true,
    }));

    const allTemp = leading.length + current.length;
    const trailingNeeded = (7 - (allTemp % 7 || 7)) % 7;
    const trailing: DayCell[] =
      trailingNeeded > 0
        ? Array.from({ length: trailingNeeded }, (_, i) => ({
            date: new Date(year, month + 1, i + 1),
            isCurrentMonth: false,
          }))
        : [];

    const allDays = [...leading, ...current, ...trailing];

    const weeks: DayCell[][] = [];

    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }

    return { weeks, messageByDate, messageCountByDate, failedMessageCountByDate, visibleFailedCountByDate };
  }, [messages, postsPerDay, year, month, userWeekStartDay]);

  return computed;
};
